import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  dashboardShareAPI,
  DashboardShare,
  Dashboard,
} from "../services/api/api";
import { Expense, Income, Debt, Goal } from "../lib/types";
import * as expenseUtils from "../lib/expenseUtils";
import * as incomeUtils from "../lib/incomeUtils";

// Dark theme for shared view
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#0f0f0f",
      paper: "#1a1a1a",
    },
    primary: {
      main: "#3182CE",
    },
  },
});

// Breakdown item interface for modals
interface BreakdownItem {
  name: string;
  amount: number;
  date?: Date | string;
  type?: string;
  isReceived?: boolean;
}

interface GroupedBreakdownItem {
  groupName: string;
  items: BreakdownItem[];
  totalAmount: number;
}

interface NestedGroupedBreakdownItem {
  categoryName: string;
  vendors: GroupedBreakdownItem[];
  totalAmount: number;
}

// Helper to format dates
const formatDateUTC = (date: Date | string | undefined): string => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

// Helper function to group items by type
const groupBreakdownItemsByType = (
  items: BreakdownItem[]
): GroupedBreakdownItem[] => {
  const groups = new Map<string, BreakdownItem[]>();
  items.forEach((item) => {
    const groupName = item.type || "Uncategorized";
    if (!groups.has(groupName)) {
      groups.set(groupName, []);
    }
    groups.get(groupName)!.push(item);
  });

  const result: GroupedBreakdownItem[] = [];
  groups.forEach((groupItems, groupName) => {
    result.push({
      groupName,
      items: groupItems.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)),
      totalAmount: groupItems.reduce((sum, item) => sum + item.amount, 0),
    });
  });

  return result.sort(
    (a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount)
  );
};

// Helper function to group items by vendor
const groupBreakdownItemsByVendor = (
  items: BreakdownItem[]
): NestedGroupedBreakdownItem[] => {
  const incomeItems = items.filter((item) => item.amount >= 0);
  const expenseItems = items.filter((item) => item.amount < 0);

  const groupByVendor = (
    categoryItems: BreakdownItem[]
  ): GroupedBreakdownItem[] => {
    const groups = new Map<string, BreakdownItem[]>();
    categoryItems.forEach((item) => {
      const separatorIndex = item.name.indexOf(" - ");
      const groupName =
        separatorIndex > 0
          ? item.name.substring(0, separatorIndex).trim()
          : item.name;
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(item);
    });

    const result: GroupedBreakdownItem[] = [];
    groups.forEach((groupItems, groupName) => {
      result.push({
        groupName,
        items: groupItems.sort(
          (a, b) => Math.abs(b.amount) - Math.abs(a.amount)
        ),
        totalAmount: groupItems.reduce((sum, item) => sum + item.amount, 0),
      });
    });

    return result.sort(
      (a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount)
    );
  };

  const result: NestedGroupedBreakdownItem[] = [];
  if (incomeItems.length > 0) {
    result.push({
      categoryName: "Income",
      vendors: groupByVendor(incomeItems),
      totalAmount: incomeItems.reduce((sum, item) => sum + item.amount, 0),
    });
  }
  if (expenseItems.length > 0) {
    result.push({
      categoryName: "Expenses",
      vendors: groupByVendor(expenseItems),
      totalAmount: expenseItems.reduce((sum, item) => sum + item.amount, 0),
    });
  }
  return result;
};

// Breakdown Modal Component
type GroupingMode = "single" | "type" | "vendor";

const SharedBreakdownModal: React.FC<{
  open: boolean;
  onClose: () => void;
  items: BreakdownItem[];
  title: string;
  formatCurrency: (amount: number) => string;
}> = ({ open, onClose, items, title, formatCurrency }) => {
  const [groupingMode, setGroupingMode] = useState<GroupingMode>("vendor");
  const [expanded, setExpanded] = useState<string | false>(false);
  const [expandedVendor, setExpandedVendor] = useState<string | false>(false);

  const displayItems = useMemo(() => {
    const sortedItems = [...items].sort(
      (a, b) => Math.abs(b.amount) - Math.abs(a.amount)
    );
    switch (groupingMode) {
      case "type":
        return groupBreakdownItemsByType(sortedItems);
      case "single":
      default:
        return sortedItems.map((item) => ({
          groupName: item.name,
          items: [item],
          totalAmount: item.amount,
        }));
    }
  }, [items, groupingMode]);

  const vendorDisplayItems = useMemo(() => {
    const sortedItems = [...items].sort(
      (a, b) => Math.abs(b.amount) - Math.abs(a.amount)
    );
    return groupBreakdownItemsByVendor(sortedItems);
  }, [items]);

  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);

  const handleAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleVendorAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedVendor(isExpanded ? panel : false);
    };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: GroupingMode
  ) => {
    setGroupingMode(newValue);
    setExpanded(false);
    setExpandedVendor(false);
  };

  // Render single item
  const renderSingleItem = (item: BreakdownItem, key: string | number) => (
    <Box
      key={key}
      sx={{
        backgroundColor: "#262626",
        mb: 1,
        borderRadius: "8px",
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 600, color: "#ffffff" }}>
            {item.name}
          </Typography>
          {item.isReceived && (
            <Typography
              variant="caption"
              sx={{
                backgroundColor: "#38A16920",
                color: "#38A169",
                px: 1,
                py: 0.25,
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "11px",
              }}
            >
              Received
            </Typography>
          )}
        </Box>
        {item.date && (
          <Typography variant="caption" sx={{ color: "#6b7280" }}>
            {formatDateUTC(item.date)}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          fontWeight: 600,
          color: item.amount >= 0 ? "#10b981" : "#E53E3E",
        }}
      >
        {formatCurrency(item.amount)}
      </Typography>
    </Box>
  );

  // Render group accordion
  const renderGroupAccordion = (group: GroupedBreakdownItem, index: number) => (
    <Accordion
      key={group.groupName}
      expanded={expanded === `panel-${index}`}
      onChange={handleAccordionChange(`panel-${index}`)}
      sx={{
        backgroundColor: "#262626",
        "&:before": { display: "none" },
        mb: 1,
        borderRadius: "8px !important",
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#9ca3af" }} />}
        sx={{
          "& .MuiAccordionSummary-content": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mr: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 600, color: "#ffffff" }}>
            {group.groupName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              backgroundColor: "#374151",
              px: 1,
              py: 0.25,
              borderRadius: "12px",
              color: "#9ca3af",
            }}
          >
            {group.items.length} items
          </Typography>
        </Box>
        <Typography
          sx={{
            fontWeight: 600,
            color: group.totalAmount >= 0 ? "#10b981" : "#E53E3E",
          }}
        >
          {formatCurrency(group.totalAmount)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: "#1f1f1f", p: 0 }}>
        {group.items.map((item, itemIndex) => (
          <Box
            key={itemIndex}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
              px: 2,
              borderBottom:
                itemIndex < group.items.length - 1
                  ? "1px solid rgba(255,255,255,0.05)"
                  : "none",
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ color: "#e5e7eb" }}>
                {item.name}
              </Typography>
              {item.date && (
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  {formatDateUTC(item.date)}
                </Typography>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: item.amount >= 0 ? "#10b981" : "#E53E3E",
              }}
            >
              {formatCurrency(item.amount)}
            </Typography>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );

  // Render vendor category (nested accordion)
  const renderVendorCategory = (
    category: NestedGroupedBreakdownItem,
    categoryIndex: number
  ) => (
    <Accordion
      key={category.categoryName}
      expanded={expanded === `category-${categoryIndex}`}
      onChange={handleAccordionChange(`category-${categoryIndex}`)}
      sx={{
        backgroundColor: "#2d2d2d",
        "&:before": { display: "none" },
        mb: 1,
        borderRadius: "8px !important",
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#9ca3af" }} />}
        sx={{
          "& .MuiAccordionSummary-content": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mr: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: "1rem", color: "#ffffff" }}
          >
            {category.categoryName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              backgroundColor: "#374151",
              px: 1,
              py: 0.25,
              borderRadius: "12px",
              color: "#9ca3af",
            }}
          >
            {category.vendors.length} vendors
          </Typography>
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            color: category.totalAmount >= 0 ? "#10b981" : "#E53E3E",
          }}
        >
          {formatCurrency(category.totalAmount)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: "#1f1f1f", p: 1 }}>
        {category.vendors.map((vendor, vendorIndex) => (
          <Accordion
            key={vendor.groupName}
            expanded={
              expandedVendor ===
              `category-${categoryIndex}-vendor-${vendorIndex}`
            }
            onChange={handleVendorAccordionChange(
              `category-${categoryIndex}-vendor-${vendorIndex}`
            )}
            sx={{
              backgroundColor: "#262626",
              "&:before": { display: "none" },
              mb: 0.5,
              borderRadius: "6px !important",
              overflow: "hidden",
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
              }
              sx={{
                minHeight: 40,
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mr: 1,
                  my: 0.5,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "#e5e7eb",
                  }}
                >
                  {vendor.groupName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: "#374151",
                    px: 0.75,
                    py: 0.125,
                    borderRadius: "10px",
                    color: "#9ca3af",
                    fontSize: "0.7rem",
                  }}
                >
                  {vendor.items.length}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: vendor.totalAmount >= 0 ? "#10b981" : "#E53E3E",
                }}
              >
                {formatCurrency(vendor.totalAmount)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: "#1a1a1a", p: 0 }}>
              {vendor.items.map((item, itemIndex) => (
                <Box
                  key={itemIndex}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    px: 2,
                    borderBottom:
                      itemIndex < vendor.items.length - 1
                        ? "1px solid rgba(255,255,255,0.05)"
                        : "none",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "#d1d5db", fontSize: "0.8rem" }}
                    >
                      {item.name}
                    </Typography>
                    {item.date && (
                      <Typography
                        variant="caption"
                        sx={{ color: "#6b7280", fontSize: "0.7rem" }}
                      >
                        {formatDateUTC(item.date)}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: item.amount >= 0 ? "#10b981" : "#E53E3E",
                    }}
                  >
                    {formatCurrency(item.amount)}
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1a1a1a",
          backgroundImage:
            "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            {items.length} items · Total: {formatCurrency(totalAmount)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: "rgba(255,255,255,0.1)" }}>
        <Tabs
          value={groupingMode}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              fontSize: "0.8rem",
              textTransform: "none",
              color: "#9ca3af",
              "&.Mui-selected": { color: "#ffffff" },
            },
            "& .MuiTabs-indicator": { backgroundColor: "#3b82f6" },
          }}
        >
          <Tab label="By Vendor" value="vendor" />
          <Tab label="By Type" value="type" />
          <Tab label="Single Items" value="single" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {items.length === 0 ? (
          <Typography variant="body2" sx={{ color: "#9ca3af", py: 2 }}>
            No items to display
          </Typography>
        ) : groupingMode === "vendor" ? (
          vendorDisplayItems.map((category, index) =>
            renderVendorCategory(category, index)
          )
        ) : groupingMode === "single" ? (
          displayItems.map((group, index) =>
            renderSingleItem(group.items[0], index)
          )
        ) : (
          displayItems.map((group, index) => renderGroupAccordion(group, index))
        )}
      </DialogContent>
    </Dialog>
  );
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Math.abs(amount));
};

interface BankAccountSnapshot {
  name: string;
  officialName?: string | null;
  type: string;
  balance: number;
  available?: number | null;
  lastSynced?: Date | string;
  institution?: string | null;
  mask?: string;
}

interface FinancialData {
  debts: Debt[];
  expenses: Expense[];
  income: Income[];
  goals: Goal[];
  bankAccounts?: BankAccountSnapshot[];
}

interface DashboardSection {
  id: string;
  label: string;
  title?: string;
  cards: { id: string; label: string }[];
}

// Default card colors
const DEFAULT_CARD_COLORS: Record<string, string> = {
  "remaining-expenses": "#E53E3E",
  "remaining-income": "#38A169",
  "net-cash-flow": "#3182CE",
  "total-expenses": "#E53E3E",
  "total-income": "#38A169",
  "credit-utilization": "#3182CE",
  "checking-balance": "#38A169",
  "savings-balance": "#38A169",
  "monthly-debt-payments": "#E53E3E",
  "personal-debts": "#E53E3E",
  "business-debts": "#E53E3E",
  "credit-cards": "#E53E3E",
  "expected-expenses": "#E53E3E",
  "expected-income": "#38A169",
  "upcoming-debt-payments": "#E53E3E",
};

const SharedDashboardPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [shareInfo, setShareInfo] = useState<DashboardShare | null>(null);
  const [password, setPassword] = useState("");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [financialData, setFinancialData] = useState<FinancialData | null>(
    null
  );
  const [isLive, setIsLive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [openBreakdownModal, setOpenBreakdownModal] = useState<{
    items: BreakdownItem[];
    title: string;
  } | null>(null);

  // Load share info on mount
  useEffect(() => {
    const loadShareInfo = async () => {
      if (!shareId) {
        setError("Invalid share link");
        setLoading(false);
        return;
      }

      try {
        const info = await dashboardShareAPI.getInfo(shareId);
        setShareInfo(info);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load share");
      } finally {
        setLoading(false);
      }
    };

    loadShareInfo();
  }, [shareId]);

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!shareId || !password) return;

    setValidating(true);
    setError(null);

    try {
      const result = await dashboardShareAPI.validate(shareId, password);
      console.debug("[SharedDashboard] Raw validate result financialData:", {
        incomeCount: result.financialData?.income?.length,
        expensesCount: result.financialData?.expenses?.length,
        debtsCount: result.financialData?.debts?.length,
        incomeSample: result.financialData?.income?.[0],
        debtSample: result.financialData?.debts?.[0],
      });
      setDashboard(result.dashboard);
      setFinancialData(result.financialData);
      setIsLive(result.isLive);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid password");
    } finally {
      setValidating(false);
    }
  };

  // Calculate card values based on financial data
  const cardValues = useMemo(() => {
    if (!financialData) return {} as Record<string, string>;

    const { debts, expenses, income, bankAccounts } = financialData;

    // Compute bank balances if available
    const checkingAccounts = (bankAccounts || []).filter(
      (a) => a.type === "checking"
    );
    const savingsAccounts = (bankAccounts || []).filter(
      (a) => a.type === "savings"
    );
    const totalCheckingBalance = checkingAccounts.reduce(
      (sum, a) => sum + Number(a.balance || 0),
      0
    );
    const totalSavingsBalance = savingsAccounts.reduce(
      (sum, a) => sum + Number(a.balance || 0),
      0
    );

    console.debug("[SharedDashboard] cardValues input data:", {
      debtsCount: debts.length,
      expensesCount: expenses.length,
      incomeCount: income.length,
      bankAccountsCount: bankAccounts?.length || 0,
      totalCheckingBalance,
      totalSavingsBalance,
      incomeSample: income[0]
        ? {
            id: income[0].id,
            client: income[0].client,
            invoiceTotal: income[0].invoiceTotal,
            expectedByDate: income[0].expectedByDate,
            expectedByDateType: typeof income[0].expectedByDate,
            expectedByDateIsDate: income[0].expectedByDate instanceof Date,
            isPaid: income[0].isPaid,
            paidToDate: income[0].paidToDate,
          }
        : "no income",
      debtSample: debts[0]
        ? {
            id: debts[0].id,
            account: debts[0].account,
            balance: debts[0].balance,
            dueDate: debts[0].dueDate,
            dueDateType: typeof debts[0].dueDate,
            maxMonthlyPayment: debts[0].maxMonthlyPayment,
            paydownAmount: debts[0].paydownAmount,
          }
        : "no debts",
    });

    // Current month totals using shared utils
    const totalMonthExpenses = expenseUtils.getCurrentMonthExpensesTotal(
      expenses,
      debts
    );
    const remainingMonthExpenses = expenseUtils.getRemainingMonthExpensesTotal(
      expenses,
      debts
    );
    const totalMonthIncome = incomeUtils.getCurrentMonthIncomeTotal(income);
    const remainingMonthIncome =
      incomeUtils.getRemainingMonthIncomeTotal(income);

    console.debug("[SharedDashboard] Computed month totals:", {
      totalMonthExpenses,
      remainingMonthExpenses,
      totalMonthIncome,
      remainingMonthIncome,
    });

    // Actual cash flow (paid income - paid expenses)
    const actualReceivedIncome = incomeUtils
      .getCurrentMonthIncome(income)
      .filter((inc) => Boolean(inc.receivedDate))
      .reduce((sum, inc) => sum + incomeUtils.getInvoiceTotal(inc), 0);
    const actualPaidExpenses = expenseUtils
      .getCurrentMonthExpenses(expenses)
      .filter((exp) => exp.isPaid && !exp.isSkipped)
      .reduce((sum, exp) => sum + Number(exp.amount), 0);

    // Timeline filters
    const today = new Date();
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const tenDaysFromNow = new Date(today);
    tenDaysFromNow.setDate(tenDaysFromNow.getDate() + 10);
    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

    const filterByDateRange = (
      dueDate: string | Date | undefined,
      start: Date,
      end: Date
    ): boolean => {
      if (!dueDate) return false;
      const d = new Date(dueDate);
      return d >= start && d <= end;
    };

    // Next 30 days
    const upcomingExpensesTotal = expenses
      .filter(
        (e) =>
          filterByDateRange(e.dueDate, today, thirtyDaysFromNow) && !e.isPaid
      )
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);
    const upcomingIncomeTotal = income
      .filter(
        (i) =>
          filterByDateRange(i.expectedByDate, today, thirtyDaysFromNow) &&
          !i.isPaid
      )
      .reduce(
        (sum, i) =>
          sum + (incomeUtils.getInvoiceTotal(i) - Number(i.paidToDate || 0)),
        0
      );
    const upcomingDebtPaymentsTotal = debts
      .filter(
        (d) =>
          filterByDateRange(d.dueDate, today, thirtyDaysFromNow) &&
          Number(d.balance || 0) > 0
      )
      .reduce(
        (sum, d) => sum + Number(d.paydownAmount || d.maxMonthlyPayment || 0),
        0
      );

    // Next 10 days
    const next10ExpensesTotal = expenses
      .filter(
        (e) => filterByDateRange(e.dueDate, today, tenDaysFromNow) && !e.isPaid
      )
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);
    const next10IncomeTotal = income
      .filter(
        (i) =>
          filterByDateRange(i.expectedByDate, today, tenDaysFromNow) &&
          !i.isPaid
      )
      .reduce(
        (sum, i) =>
          sum + (incomeUtils.getInvoiceTotal(i) - Number(i.paidToDate || 0)),
        0
      );
    const next10DebtPaymentsTotal = debts
      .filter(
        (d) =>
          filterByDateRange(d.dueDate, today, tenDaysFromNow) &&
          Number(d.balance || 0) > 0
      )
      .reduce(
        (sum, d) => sum + Number(d.paydownAmount || d.maxMonthlyPayment || 0),
        0
      );

    // Next 7 days
    const next7ExpensesTotal = expenses
      .filter(
        (e) =>
          filterByDateRange(e.dueDate, today, sevenDaysFromNow) && !e.isPaid
      )
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);
    const next7IncomeTotal = income
      .filter(
        (i) =>
          filterByDateRange(i.expectedByDate, today, sevenDaysFromNow) &&
          !i.isPaid
      )
      .reduce(
        (sum, i) =>
          sum + (incomeUtils.getInvoiceTotal(i) - Number(i.paidToDate || 0)),
        0
      );
    const next7DebtPaymentsTotal = debts
      .filter(
        (d) =>
          filterByDateRange(d.dueDate, today, sevenDaysFromNow) &&
          Number(d.balance || 0) > 0
      )
      .reduce(
        (sum, d) => sum + Number(d.paydownAmount || d.maxMonthlyPayment || 0),
        0
      );

    // Next month
    const nextMonthStart = new Date(
      Date.UTC(today.getFullYear(), today.getMonth() + 1, 1)
    );
    const nextMonthEnd = new Date(
      Date.UTC(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59, 999)
    );
    const nextMonthExpensesTotal = expenses
      .filter(
        (e) =>
          filterByDateRange(e.dueDate, nextMonthStart, nextMonthEnd) &&
          !e.isPaid
      )
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);
    const nextMonthIncomeTotal = income
      .filter(
        (i) =>
          filterByDateRange(i.expectedByDate, nextMonthStart, nextMonthEnd) &&
          !i.isPaid
      )
      .reduce((sum, i) => sum + incomeUtils.getInvoiceTotal(i), 0);
    const nextMonthDebtPaymentsTotal = debts
      .filter(
        (d) =>
          filterByDateRange(d.dueDate, nextMonthStart, nextMonthEnd) &&
          Number(d.balance || 0) > 0
      )
      .reduce(
        (sum, d) => sum + Number(d.paydownAmount || d.maxMonthlyPayment || 0),
        0
      );

    // Annual calculations
    const currentYear = today.getFullYear();
    const pastYear = currentYear - 1;
    const yearStart = new Date(Date.UTC(currentYear, 0, 1));
    const yearEnd = new Date(Date.UTC(currentYear + 1, 0, 1));
    const pastYearStart = new Date(Date.UTC(pastYear, 0, 1));
    const pastYearEnd = new Date(Date.UTC(pastYear + 1, 0, 1));

    const currentYearIncome = income
      .filter((i) => {
        const d = i.expectedByDate ? new Date(i.expectedByDate) : null;
        return d && d >= yearStart && d < yearEnd;
      })
      .reduce((sum, i) => sum + incomeUtils.getInvoiceTotal(i), 0);

    const currentYearExpenses = expenses
      .filter((e) => {
        const d = e.dueDate ? new Date(e.dueDate) : null;
        return d && d >= yearStart && d < yearEnd;
      })
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);

    const pastYearIncome = income
      .filter((i) => {
        const d = i.expectedByDate ? new Date(i.expectedByDate) : null;
        return d && d >= pastYearStart && d < pastYearEnd;
      })
      .reduce((sum, i) => sum + incomeUtils.getInvoiceTotal(i), 0);

    const pastYearExpenses = expenses
      .filter((e) => {
        const d = e.dueDate ? new Date(e.dueDate) : null;
        return d && d >= pastYearStart && d < pastYearEnd;
      })
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);

    // Projected income (extrapolate from YTD daily average)
    const todayUtc = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
    );
    const daysInYear = Math.floor(
      (yearEnd.getTime() - yearStart.getTime()) / 86400000
    );
    const dayOfYear = Math.max(
      1,
      Math.floor((todayUtc.getTime() - yearStart.getTime()) / 86400000) + 1
    );
    const projectedIncome =
      dayOfYear > 0 ? (currentYearIncome / dayOfYear) * daysInYear : 0;

    // Credit utilization
    const totalCreditLimit = debts
      .filter((d) => d.type === "Credit Card")
      .reduce((sum, d) => sum + Number(d.creditLimit || 0), 0);
    const totalCreditBalance = debts
      .filter((d) => d.type === "Credit Card")
      .reduce((sum, d) => sum + Number(d.balance || 0), 0);
    const creditUtilization =
      totalCreditLimit > 0
        ? Math.round((totalCreditBalance / totalCreditLimit) * 100)
        : 0;

    // Debt category totals
    const personalDebtTotal = debts
      .filter((d) => {
        const type = d.type?.toLowerCase() || "";
        return type === "personal loan" || type === "other";
      })
      .reduce((sum, d) => sum + Number(d.balance || 0), 0);
    const businessDebtTotal = debts
      .filter((d) => d.type?.toLowerCase().includes("business"))
      .reduce((sum, d) => sum + Number(d.balance || 0), 0);
    const creditCardTotal = debts
      .filter((d) => d.type === "Credit Card")
      .reduce((sum, d) => sum + Math.abs(Number(d.balance || 0)), 0);

    console.debug("[SharedDashboard] Card values computed", {
      totalMonthExpenses,
      totalMonthIncome,
      upcomingExpensesTotal,
      next7ExpensesTotal,
      currentYearIncome,
    });

    return {
      // This Month
      "total-expenses": formatCurrency(totalMonthExpenses),
      "remaining-expenses": formatCurrency(remainingMonthExpenses),
      "total-income": formatCurrency(totalMonthIncome),
      "remaining-income": formatCurrency(remainingMonthIncome),
      "net-cash-flow": formatCurrency(
        actualReceivedIncome - actualPaidExpenses
      ),
      "credit-utilization": `${creditUtilization}%`,

      // Bank accounts (from snapshot if available)
      "checking-balance":
        bankAccounts && bankAccounts.length > 0
          ? formatCurrency(totalCheckingBalance)
          : "N/A",
      "savings-balance":
        bankAccounts && bankAccounts.length > 0
          ? formatCurrency(totalSavingsBalance)
          : "N/A",

      // Debts
      "monthly-debt-payments": formatCurrency(
        debts.reduce((sum, d) => sum + Number(d.maxMonthlyPayment || 0), 0)
      ),
      "personal-debts": formatCurrency(personalDebtTotal),
      "business-debts": formatCurrency(businessDebtTotal),
      "credit-cards": formatCurrency(creditCardTotal),

      // Next Month
      "next-month-expenses": formatCurrency(nextMonthExpensesTotal),
      "next-month-income": formatCurrency(nextMonthIncomeTotal),
      "next-month-debt-payments": formatCurrency(nextMonthDebtPaymentsTotal),

      // Within 30 Days
      "expected-expenses": formatCurrency(upcomingExpensesTotal),
      "expected-income": formatCurrency(upcomingIncomeTotal),
      "upcoming-debt-payments": formatCurrency(upcomingDebtPaymentsTotal),

      // Within 10 Days
      "next-10-expenses": formatCurrency(next10ExpensesTotal),
      "next-10-income": formatCurrency(next10IncomeTotal),
      "next-10-debt-payments": formatCurrency(next10DebtPaymentsTotal),

      // Within 7 Days
      "next-7-expenses": formatCurrency(next7ExpensesTotal),
      "next-7-income": formatCurrency(next7IncomeTotal),
      "next-7-debt-payments": formatCurrency(next7DebtPaymentsTotal),

      // Annually
      "current-year-income": formatCurrency(currentYearIncome),
      "projected-income-2026": formatCurrency(projectedIncome),
      "current-year-expenses": formatCurrency(currentYearExpenses),
      "past-year-income": formatCurrency(pastYearIncome),
      "past-year-expenses": formatCurrency(pastYearExpenses),
    };
  }, [financialData]);

  // Debug: log final cardValues whenever they change
  console.debug("[SharedDashboard] Final cardValues:", cardValues);

  // Compute breakdown items for each card
  const cardBreakdowns = useMemo((): Record<string, BreakdownItem[]> => {
    if (!financialData) return {};

    const { debts, expenses, income } = financialData;
    const today = new Date();
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const tenDaysFromNow = new Date(today);
    tenDaysFromNow.setDate(tenDaysFromNow.getDate() + 10);
    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    const nextMonthStart = new Date(
      Date.UTC(today.getFullYear(), today.getMonth() + 1, 1)
    );
    const nextMonthEnd = new Date(
      Date.UTC(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59, 999)
    );
    const currentYear = today.getFullYear();
    const pastYear = currentYear - 1;
    const yearStart = new Date(Date.UTC(currentYear, 0, 1));
    const yearEnd = new Date(Date.UTC(currentYear + 1, 0, 1));
    const pastYearStart = new Date(Date.UTC(pastYear, 0, 1));
    const pastYearEnd = new Date(Date.UTC(pastYear + 1, 0, 1));

    const filterByDateRange = (
      dueDate: string | Date | undefined,
      start: Date,
      end: Date
    ): boolean => {
      if (!dueDate) return false;
      const d = new Date(dueDate);
      return d >= start && d <= end;
    };

    // Current month helpers
    const currentMonthExpenses = expenseUtils.getCurrentMonthExpenses(expenses);
    const currentMonthIncome = incomeUtils.getCurrentMonthIncome(income);
    const remainingExpenses = expenseUtils.getRemainingMonthExpenses(expenses);
    const remainingIncome = incomeUtils.getRemainingMonthIncome(income);

    // Map expenses to breakdown items
    const mapExpense = (e: Expense): BreakdownItem => ({
      name: e.name || "Unknown Expense",
      amount: -Number(e.amount || 0),
      date: e.dueDate,
      type: "Expense",
      isReceived: e.isPaid,
    });

    // Map income to breakdown items
    const mapIncome = (i: Income): BreakdownItem => ({
      name: i.invoiceNumber
        ? `${i.client || "Unknown"} - ${i.invoiceNumber}`
        : i.client || "Unknown",
      amount: incomeUtils.getInvoiceTotal(i) - Number(i.paidToDate || 0),
      date: i.expectedByDate,
      type: i.type || "Income",
      isReceived: Boolean(i.receivedDate),
    });

    // Map debt to breakdown item
    const mapDebt = (d: Debt): BreakdownItem => ({
      name:
        d.creditor && d.account
          ? `${d.creditor} - ${d.account}`
          : d.creditor || d.account || "Unknown",
      amount: -Number(d.paydownAmount || d.maxMonthlyPayment || 0),
      date: d.dueDate,
      type: d.type || "Debt",
    });

    return {
      // This Month
      "total-expenses": currentMonthExpenses.map(mapExpense),
      "remaining-expenses": remainingExpenses.map(mapExpense),
      "total-income": currentMonthIncome.map(mapIncome),
      "remaining-income": remainingIncome.map(mapIncome),
      "net-cash-flow": [
        ...currentMonthIncome.map(mapIncome),
        ...currentMonthExpenses.map(mapExpense),
      ],

      // Debts
      "monthly-debt-payments": debts.map(mapDebt),
      "personal-debts": debts
        .filter((d) => {
          const type = d.type?.toLowerCase() || "";
          return type === "personal loan" || type === "other";
        })
        .map((d) => ({
          name:
            d.creditor && d.account
              ? `${d.creditor} - ${d.account}`
              : d.creditor || d.account || "Unknown",
          amount: -Number(d.balance || 0),
          type: d.type || "Debt",
        })),
      "business-debts": debts
        .filter((d) => d.type?.toLowerCase().includes("business"))
        .map((d) => ({
          name:
            d.creditor && d.account
              ? `${d.creditor} - ${d.account}`
              : d.creditor || d.account || "Unknown",
          amount: -Number(d.balance || 0),
          type: d.type || "Debt",
        })),
      "credit-cards": debts
        .filter((d) => d.type === "Credit Card")
        .map((d) => ({
          name:
            d.creditor && d.account
              ? `${d.creditor} - ${d.account}`
              : d.creditor || d.account || "Unknown",
          amount: -Math.abs(Number(d.balance || 0)),
          type: "Credit Card",
        })),

      // Within 30 Days
      "expected-expenses": expenses
        .filter(
          (e) =>
            filterByDateRange(e.dueDate, today, thirtyDaysFromNow) && !e.isPaid
        )
        .map(mapExpense),
      "expected-income": income
        .filter(
          (i) =>
            filterByDateRange(i.expectedByDate, today, thirtyDaysFromNow) &&
            !i.isPaid
        )
        .map(mapIncome),
      "upcoming-debt-payments": debts
        .filter(
          (d) =>
            filterByDateRange(d.dueDate, today, thirtyDaysFromNow) &&
            Number(d.balance || 0) > 0
        )
        .map(mapDebt),

      // Within 10 Days
      "next-10-expenses": expenses
        .filter(
          (e) =>
            filterByDateRange(e.dueDate, today, tenDaysFromNow) && !e.isPaid
        )
        .map(mapExpense),
      "next-10-income": income
        .filter(
          (i) =>
            filterByDateRange(i.expectedByDate, today, tenDaysFromNow) &&
            !i.isPaid
        )
        .map(mapIncome),
      "next-10-debt-payments": debts
        .filter(
          (d) =>
            filterByDateRange(d.dueDate, today, tenDaysFromNow) &&
            Number(d.balance || 0) > 0
        )
        .map(mapDebt),

      // Within 7 Days
      "next-7-expenses": expenses
        .filter(
          (e) =>
            filterByDateRange(e.dueDate, today, sevenDaysFromNow) && !e.isPaid
        )
        .map(mapExpense),
      "next-7-income": income
        .filter(
          (i) =>
            filterByDateRange(i.expectedByDate, today, sevenDaysFromNow) &&
            !i.isPaid
        )
        .map(mapIncome),
      "next-7-debt-payments": debts
        .filter(
          (d) =>
            filterByDateRange(d.dueDate, today, sevenDaysFromNow) &&
            Number(d.balance || 0) > 0
        )
        .map(mapDebt),

      // Next Month
      "next-month-expenses": expenses
        .filter(
          (e) =>
            filterByDateRange(e.dueDate, nextMonthStart, nextMonthEnd) &&
            !e.isPaid
        )
        .map(mapExpense),
      "next-month-income": income
        .filter(
          (i) =>
            filterByDateRange(i.expectedByDate, nextMonthStart, nextMonthEnd) &&
            !i.isPaid
        )
        .map(mapIncome),
      "next-month-debt-payments": debts
        .filter(
          (d) =>
            filterByDateRange(d.dueDate, nextMonthStart, nextMonthEnd) &&
            Number(d.balance || 0) > 0
        )
        .map(mapDebt),

      // Annually
      "current-year-income": income
        .filter((i) => {
          const d = i.expectedByDate ? new Date(i.expectedByDate) : null;
          return d && d >= yearStart && d < yearEnd;
        })
        .map(mapIncome),
      "current-year-expenses": expenses
        .filter((e) => {
          const d = e.dueDate ? new Date(e.dueDate) : null;
          return d && d >= yearStart && d < yearEnd;
        })
        .map(mapExpense),
      "past-year-income": income
        .filter((i) => {
          const d = i.expectedByDate ? new Date(i.expectedByDate) : null;
          return d && d >= pastYearStart && d < pastYearEnd;
        })
        .map(mapIncome),
      "past-year-expenses": expenses
        .filter((e) => {
          const d = e.dueDate ? new Date(e.dueDate) : null;
          return d && d >= pastYearStart && d < pastYearEnd;
        })
        .map(mapExpense),
    };
  }, [financialData]);

  // Handle card click
  const handleCardClick = useCallback(
    (cardId: string, cardLabel: string) => {
      const items = cardBreakdowns[cardId];
      if (items && items.length > 0) {
        setOpenBreakdownModal({ items, title: cardLabel });
      }
    },
    [cardBreakdowns]
  );

  // Get card color
  const getCardColor = (cardId: string): string => {
    if (!dashboard?.coloringEnabled) {
      return "#718096";
    }
    return (
      dashboard?.cardColors?.[cardId] ||
      DEFAULT_CARD_COLORS[cardId] ||
      "#3182CE"
    );
  };

  // Get section column count
  const getSectionColumns = (sectionId: string, cardCount: number): number => {
    if (dashboard?.sectionColumns?.[sectionId]) {
      return dashboard.sectionColumns[sectionId];
    }
    if (cardCount <= 2) return cardCount;
    if (cardCount <= 4) return cardCount;
    if (cardCount <= 6) return 3;
    return 4;
  };

  // Check if card should be hidden
  const isCardHidden = (cardId: string, sectionId: string): boolean => {
    const visibility = dashboard?.visibility;
    if (!visibility) return false;
    return (
      visibility.hiddenCards?.[cardId] ||
      visibility.hiddenGroups?.[sectionId] ||
      false
    );
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#0f0f0f",
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" sx={{ color: "white", mb: 1 }}>
              {shareInfo?.dashboardName || "Shared Dashboard"}
            </Typography>
            {shareInfo?.dashboardDescription && (
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}
              >
                {shareInfo.dashboardDescription}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 1,
              }}
            >
              {isAuthenticated && (
                <Chip
                  label={isLive ? "Live Data" : "Snapshot"}
                  color={isLive ? "success" : "warning"}
                  size="small"
                />
              )}
              {shareInfo?.expiresAt && (
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.6)" }}
                >
                  Expires:{" "}
                  {new Date(shareInfo.expiresAt).toLocaleDateString("en-US", {
                    timeZone: "UTC",
                  })}
                </Typography>
              )}
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error && !isAuthenticated ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Alert severity="error" sx={{ maxWidth: 400 }}>
                {error}
              </Alert>
            </Box>
          ) : !isAuthenticated ? (
            // Password prompt
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <Card sx={{ maxWidth: 400, width: "100%" }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <LockIcon
                      sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ textAlign: "center" }}>
                      Password Protected
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mt: 1,
                      }}
                    >
                      Enter the password to view this shared dashboard
                    </Typography>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmitPassword}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      sx={{ mb: 2 }}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={validating || !password}
                    >
                      {validating ? (
                        <CircularProgress size={24} />
                      ) : (
                        "View Dashboard"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </Box>
          ) : (
            // Authenticated view - show dashboard
            <>
              {dashboard?.layout &&
                (dashboard.layout as DashboardSection[]).map((section) => {
                  const visibleCards = section.cards.filter(
                    (card) => !isCardHidden(card.id, section.id)
                  );
                  if (visibleCards.length === 0) return null;

                  const columns = getSectionColumns(
                    section.id,
                    visibleCards.length
                  );
                  const gridColumns = Math.floor(12 / columns);

                  return (
                    <Box key={section.id} sx={{ mb: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "white",
                          mb: 2,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          fontSize: "0.875rem",
                        }}
                      >
                        {section.label || section.title || "Untitled section"}
                      </Typography>
                      <Grid container spacing={2}>
                        {visibleCards.map((card) => {
                          const cardColor = getCardColor(card.id);
                          const value = cardValues[card.id] || "—";
                          if (!cardValues[card.id]) {
                            console.warn(
                              `[SharedDashboard] Missing card value for "${card.id}" (label: "${card.label}"). Available keys:`,
                              Object.keys(cardValues)
                            );
                          }

                          const hasBreakdown =
                            cardBreakdowns[card.id] &&
                            cardBreakdowns[card.id].length > 0;

                          return (
                            <Grid
                              size={{ xs: 12, sm: 6, md: gridColumns }}
                              key={card.id}
                            >
                              <Card
                                onClick={() =>
                                  hasBreakdown &&
                                  handleCardClick(card.id, card.label)
                                }
                                sx={{
                                  borderLeft: `4px solid ${cardColor}`,
                                  height: "100%",
                                  cursor: hasBreakdown ? "pointer" : "default",
                                  transition: "all 0.3s ease",
                                  "&:hover": hasBreakdown
                                    ? {
                                        boxShadow:
                                          "0 8px 12px 0 rgba(0, 0, 0, 0.4)",
                                        transform: "translateY(-2px)",
                                      }
                                    : {},
                                }}
                              >
                                <CardContent>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: 500,
                                      letterSpacing: "0.025em",
                                      textTransform: "uppercase",
                                      fontSize: "0.75rem",
                                      mb: 1,
                                    }}
                                  >
                                    {card.label}
                                  </Typography>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: cardColor,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {value}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  );
                })}

              {/* Summary Section */}
              {financialData && (
                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "white",
                      mb: 2,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontSize: "0.875rem",
                    }}
                  >
                    Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Card sx={{ borderTop: "4px solid #38a169" }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            Total Debts
                          </Typography>
                          <Typography variant="h5" sx={{ color: "#e53e3e" }}>
                            {financialData.debts.length} accounts
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Card sx={{ borderTop: "4px solid #dd6b20" }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            Total Expenses
                          </Typography>
                          <Typography variant="h5" sx={{ color: "#dd6b20" }}>
                            {financialData.expenses.length} items
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Card sx={{ borderTop: "4px solid #3182ce" }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            Income Sources
                          </Typography>
                          <Typography variant="h5" sx={{ color: "#3182ce" }}>
                            {financialData.income.length} items
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Footer */}
              <Box sx={{ mt: 4, textAlign: "center" }}>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.5)" }}
                >
                  This is a read-only view of shared dashboard information.
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Breakdown Modal */}
      {openBreakdownModal && (
        <SharedBreakdownModal
          open={true}
          onClose={() => setOpenBreakdownModal(null)}
          items={openBreakdownModal.items}
          title={openBreakdownModal.title}
          formatCurrency={formatCurrency}
        />
      )}
    </ThemeProvider>
  );
};

export default SharedDashboardPage;
